import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { environment as ENV } from 'src/environments/environment';
import { UtmTrackingService } from './utm-tracking.service';
import { getPageInfo } from '../config/page-type-mapping.config';

declare global {
  interface Window {
    posthog?: any;
  }
}

@Injectable({
  providedIn: 'root',
})
export class PosthogService {
  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private utmTrackingService: UtmTrackingService
  ) {
    if (isPlatformBrowser(this.platformId) && ENV.postHog.enabled) {
      this.initPosthog();
    }
  }

  private initPosthog(): void {
    if (typeof window !== 'undefined' && window.posthog) {
      window.posthog.init(ENV.postHog.apiKey, {
        api_host: ENV.postHog.apiHost,
        save_device_id: true, // Essential for session stitching across pages
        person_profiles: ENV.postHog.personProfiles || 'identified_only',
        capture_pageview: false, // Handled manually with custom page_viewed_{page_name} events
        capture_pageleave: false,
        autocapture: false,
        __disable_dead_clicks: true,
      });
    }
  }

  /**
   * Captures custom event with auto-injected standard properties
   * Standard properties include: UTM params, device info, session data, etc.
   */
  capture(eventName: string, properties?: any): void {
    if (
      isPlatformBrowser(this.platformId) &&
      window.posthog &&
      ENV.postHog.enabled
    ) {
      const standardProps = this.utmTrackingService.getStandardProperties();
      window.posthog.capture(eventName, {
        ...standardProps,
        ...properties,
      });
    }
  }

  identify(userId: string, properties?: any): void {
    if (
      isPlatformBrowser(this.platformId) &&
      window.posthog &&
      ENV.postHog.enabled
    ) {
      window.posthog.identify(userId, properties);
    }
  }

  reset(): void {
    if (
      isPlatformBrowser(this.platformId) &&
      window.posthog &&
      ENV.postHog.enabled
    ) {
      window.posthog.reset();
    }
  }

  isFeatureEnabled(featureFlag: string): boolean {
    if (
      isPlatformBrowser(this.platformId) &&
      window.posthog &&
      ENV.postHog.enabled
    ) {
      return window.posthog.isFeatureEnabled(featureFlag);
    }
    return false;
  }

  getFeatureFlag(featureFlag: string): string | boolean | undefined {
    if (
      isPlatformBrowser(this.platformId) &&
      window.posthog &&
      ENV.postHog.enabled
    ) {
      return window.posthog.getFeatureFlag(featureFlag);
    }
    return undefined;
  }

  // ========================================
  // Utility Methods
  // ========================================

  // Converts object keys from camelCase to snake_case
  private convertToSnakeCase(obj: any): any {
    if (!obj) return {};
    const result: any = {};
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        const snakeKey = key.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
        result[snakeKey] = obj[key];
      }
    }
    return result;
  }

  // ========================================
  // Website Event Tracking Methods
  // ========================================

  /**
   * Tracks page view events using PostHog native $pageview
   * Properties: page_name, page_type, page_url, page_title, + standard properties
   * 
   * page_type and page_name are derived from URL using page-type-mapping config.
   * If pageName is explicitly passed, it overrides the URL-derived page_name.
   * If pageType is explicitly passed, it overrides the URL-derived page_type.
   */
  capturePageViewEvent(pageName?: string, additionalProps?: any, pageType?: string): void {
    if (isPlatformBrowser(this.platformId)) {
      const currentUrl = typeof window !== 'undefined' ? window.location.pathname : '/';
      const pageInfo = getPageInfo(currentUrl);
      
      const snakeProps = this.convertToSnakeCase(additionalProps);
      const { page_url, page_title, ...restProps } = snakeProps;
      
      this.capture('$pageview', {
        page_name: pageName || pageInfo.page_name,
        page_type: pageType || pageInfo.page_type,
        page_url:
          page_url ||
          (typeof window !== 'undefined' ? window.location.href : ''),
        page_title:
          page_title ||
          (typeof document !== 'undefined' ? document.title : ''),
        ...restProps,
      });
    }
  }

  /**
   * Tracks CTA button clicks
   * Pattern: cta_{action}_{location}_clicked
   * Properties: cta_text, cta_location, destination_url, + standard properties
   */
  captureCTAClick(
    action: string,
    location: string,
    additionalProps?: any
  ): void {
    const snakeProps = this.convertToSnakeCase(additionalProps);
    const { cta_text, destination_url, ...restProps } = snakeProps;
    this.capture(`cta_${action}_${location}_clicked`, {
      cta_text: cta_text || action.replace(/_/g, ' '),
      cta_location: location,
      destination_url: destination_url,
      ...restProps,
    });
  }

  /**
   * Tracks navigation menu clicks
   * Pattern: nav_{menu}_{item}_clicked
   * Properties: menu_name, item_name, item_category, destination_url, + standard properties
   */
  captureNavClick(menu: string, item: string, additionalProps?: any): void {
    const snakeProps = this.convertToSnakeCase(additionalProps);
    const { item_category, destination_url, ...restProps } = snakeProps;
    this.capture(`nav_${menu}_${item}_clicked`, {
      menu_name: menu,
      item_name: item,
      item_category: item_category,
      destination_url: destination_url,
      ...restProps,
    });
  }

  /**
   * Tracks app download button clicks
   * Pattern: app_download_{platform}_{location}_clicked
   * Properties: platform, location, button_text, destination_url, + standard properties
   */
  captureAppDownloadClick(
    platform: 'ios' | 'android',
    location: string,
    additionalProps?: any
  ): void {
    const snakeProps = this.convertToSnakeCase(additionalProps);
    const { button_text, destination_url, ...restProps } = snakeProps;
    this.capture(`app_download_${platform}_${location}_clicked`, {
      platform: platform,
      location: location,
      button_text: button_text,
      destination_url: destination_url,
      ...restProps,
    });
  }

  /**
   * Tracks login button click
   * Pattern: login_clicked
   * Properties: element_location, + standard properties
   */
  captureLoginClick(additionalProps?: any): void {
    const snakeProps = this.convertToSnakeCase(additionalProps);
    const { location, ...restProps } = snakeProps;
    this.capture('login_clicked', {
      element_location: location || 'header',
      ...restProps,
    });
  }

  /**
   * Tracks case study clicks
   * Pattern: case_study_{company}_clicked
   * Properties: content_title, company_name, content_type, location, destination_url, + standard properties
   */
  captureCaseStudyClick(companyName: string, additionalProps?: any): void {
    const snakeProps = this.convertToSnakeCase(additionalProps);
    const { content_title, location, destination_url, ...restProps } = snakeProps;
    const normalizedCompany = companyName.toLowerCase().replace(/\s+/g, '_');
    this.capture(`case_study_${normalizedCompany}_clicked`, {
      content_title: content_title || companyName,
      company_name: companyName,
      content_type: 'case_study',
      location: location,
      destination_url: destination_url,
      ...restProps,
    });
  }

  /**
   * Tracks content clicks (blog posts, whitepapers, etc.)
   * Pattern: {content_type}_clicked
   * Properties: content_title, content_type, location, destination_url, + standard properties
   */
  captureContentClick(
    contentType: 'blog_post' | 'whitepaper',
    additionalProps?: any
  ): void {
    const snakeProps = this.convertToSnakeCase(additionalProps);
    const { content_title, location, destination_url, ...restProps } = snakeProps;
    this.capture(`${contentType}_clicked`, {
      content_title: content_title,
      content_type: contentType,
      location: location,
      destination_url: destination_url,
      ...restProps,
    });
  }

  /**
   * Tracks product or solution card clicks
   * Pattern: product_card_{card_name}_clicked or solution_card_{card_name}_clicked
   * Properties: card_name, card_type, card_location, destination_url, + standard properties
   */
  captureCardClick(
    cardName: string,
    cardType: 'product' | 'solution',
    location: string,
    additionalProps?: any
  ): void {
    const snakeProps = this.convertToSnakeCase(additionalProps);
    const { destination_url, ...restProps } = snakeProps;
    // Normalize card name: remove special chars, replace spaces with underscores, lowercase
    const normalizedCardName = cardName
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, '')
      .replace(/\s+/g, '_');
    
    this.capture(`${cardType}_card_${normalizedCardName}_clicked`, {
      card_name: cardName,
      card_type: cardType,
      card_location: location,
      destination_url: destination_url,
      ...restProps,
    });
  }

  /**
   * Tracks testimonial viewport visibility
   * Pattern: testimonial_viewed
   * Properties: company_name, tab_position, action_type, + standard properties
   */
  captureTestimonialView(companyName: string, position: number, additionalProps?: any): void {
    const snakeProps = this.convertToSnakeCase(additionalProps);
    this.capture('testimonial_viewed', {
      company_name: companyName,
      tab_position: position,
      action_type: 'viewed',
      ...snakeProps,
    });
  }

  /**
   * Tracks testimonial tab click (carousel navigation)
   * Pattern: testimonial_tab_clicked
   * Properties: company_name, tab_position, action_type, + standard properties
   */
  captureTestimonialTabClick(companyName: string, position: number, additionalProps?: any): void {
    const snakeProps = this.convertToSnakeCase(additionalProps);
    this.capture('testimonial_tab_clicked', {
      company_name: companyName,
      tab_position: position,
      action_type: 'tab_clicked',
      ...snakeProps,
    });
  }

/**
   * Tracks testimonial case study link clicks
   * Pattern: testimonial_case_study_clicked
   * Properties: company_name, tab_position, action_type, destination_url, + standard properties
   */
  captureTestimonialCaseStudyClick(companyName: string, position: number, additionalProps?: any): void {
    const snakeProps = this.convertToSnakeCase(additionalProps);
    const { destination_url, ...restProps } = snakeProps;
    this.capture('testimonial_case_study_clicked', {
      company_name: companyName,
      tab_position: position,
      action_type: 'case_study_clicked',
      destination_url: destination_url,
      ...restProps,
    });
  }

  /**
   * Tracks footer link clicks
   * Pattern: footer_{category}_{item}_clicked
   * Properties: footer_category, link_text, link_destination, + standard properties
   */
  captureFooterClick(category: string, linkText: string, additionalProps?: any): void {
    const snakeProps = this.convertToSnakeCase(additionalProps);
    const { link_destination, ...restProps } = snakeProps;
    // Normalize category and link text for event name
    const normalizedCategory = category.toLowerCase().replace(/[^a-z0-9\s]/g, '').replace(/\s+/g, '_');
    const normalizedItem = linkText.toLowerCase().replace(/[^a-z0-9\s]/g, '').replace(/\s+/g, '_');
    
    this.capture(`footer_${normalizedCategory}_${normalizedItem}_clicked`, {
      footer_category: category,
      link_text: linkText,
      link_destination: link_destination,
      ...restProps,
    });
  }

  /**
   * Tracks social media icon clicks (footer)
   * Pattern: footer_social_{platform}_clicked
   * Properties: footer_category, platform, link_destination, + standard properties
   */
  captureSocialClick(platform: string, additionalProps?: any): void {
    const snakeProps = this.convertToSnakeCase(additionalProps);
    const { link_destination, ...restProps } = snakeProps;
    const normalizedPlatform = platform.toLowerCase().replace(/[^a-z0-9]/g, '_');
    
    this.capture(`footer_social_${normalizedPlatform}_clicked`, {
      footer_category: 'social',
      platform: platform,
      link_destination: link_destination,
      ...restProps,
    });
  }
}
